import {isXeroInvoicingImprovementsEnabled} from '../../../forecast-app/shared/util/FeatureUtil';
import {
	XeroAccountOption,
	XeroExportLineRow,
	XeroItemOption,
	XeroLineSelection,
	XeroTrackingCategory,
} from './XeroExportLinesTable.type';
import {InvoiceEntryRow} from './ExportInvoiceModal.type';
import type {XeroClientOption} from './ExportInvoiceModal.target';
import DirectApi from '../../../directApi';
import {
	CONFIG_CACHE,
	CONFIG_IN_FLIGHT,
	clearXeroLineConfigCache,
	getContactsRead,
	setContactsRead,
} from './xeroLineConfigCache';
import Util from '../../../forecast-app/shared/util/util';

/**
 * Whether this modal renders the new Xero line table instead of its existing one.
 *
 * Resolved once per render into a local in each modal, at the point it picks a path - T4-133's
 * acceptance criteria require the flag check to live there rather than being repeated per field,
 * so that a flag-off company has no code path to the new data at all.
 *
 * Gated on `xero_integration_invoicing_improvements`, the SAME flag the xero service gates the
 * outbound line on. This briefly had a second flag of its own, `xero_invoice_line_export`, so
 * that a rollback would not also withdraw T4-122's write-back and the three configuration
 * imports - the premise being that the improvements flag was already on for config-import
 * customers. Pritesh established on 2026-09-30 that it is on for no client, which removed the
 * premise, and the second flag went with it.
 *
 * One flag also restores a guarantee the split had broken. The three endpoints this table reads
 * from - accounts, products and tracking categories - are gated on the improvements flag in
 * `graphql/src/routes/XeroRoutes.js`. Opening the table therefore guarantees the reads are open
 * again, where under two flags the line-export flag alone left all three 404ing, every dropdown
 * reading "Could not load", and the modal unusable with no way out but Cancel.
 */
export const shouldUseXeroLinesTable = (isXeroInvoice: boolean): boolean =>
	isXeroInvoice && isXeroInvoicingImprovementsEnabled();

/**
 * The description the export sends for a line: the picked item's, else the line's own.
 *
 * `||`, not `??`: an item whose description is empty has nothing to send, so the line keeps its own.
 */
export const xeroExportDescription = (row: {entryDescription?: string | null; xeroItemDescription?: string}): string | null =>
	row.xeroItemDescription || (row.entryDescription ?? null);

/**
 * The description as the xero service will write it, with the project in front when the
 * "Include project in descriptions" box is ticked. The format must match the service's.
 *
 * A line with no description shows the project alone, with no trailing space and never the
 * text "null".
 */
export const xeroLineDescription = (
	description: string | null | undefined,
	project: {companyProjectId?: number | null; name?: string | null} | null | undefined,
	projectInDescription: boolean
): string | null =>
	projectInDescription && project
		? `(P${project.companyProjectId}-${project.name})` + (description ? ` ${description}` : '')
		: description ?? null;

/**
 * `InvoiceEntryRow` and `XeroExportLineRow` overlap but are not the same type: the former
 * carries the whole Relay `InvoiceEntry`, the latter only what the table renders. Mapping
 * explicitly rather than casting keeps the table independent of the Relay schema, which is
 * what lets T4-133 point it at real data without touching the component.
 */
export const toXeroLineRows = (rows: InvoiceEntryRow[], projectInDescription = false): XeroExportLineRow[] =>
	rows.map(row => ({
		id: row.id,
		project: row.project
			? {
					id: row.project.id,
					name: row.project.name ?? '',
					companyProjectId: row.project.companyProjectId ?? undefined,
			  }
			: null,
		projectColor: row.projectColor,
		customProjectId: row.customProjectId,
		entryDescription: xeroLineDescription(xeroExportDescription(row), row.project, projectInDescription),
		itemDescription: row.xeroItemDescription,
		quantity: row.quantity ?? undefined,
		unitPrice: row.unitPrice ?? undefined,
		/*
		 * Falls back to the row's CURRENT price when the mapper did not set an original.
		 *
		 * Not defensive clutter: `changedUnitPrices` reports a line as repriced when the two differ,
		 * so an absent original makes EVERY line differ from its own price and the export rewrites
		 * every entry on the invoice. The fallback turns that into "nothing was repriced", which is
		 * the safe reading of "we do not know what this line started at".
		 *
		 * It cannot mask a real reprice: the modal sets `originalUnitPrice` when it builds the row,
		 * before any product can fill a price, so by the time the two differ the original is there.
		 */
		originalUnitPrice: row.originalUnitPrice ?? row.unitPrice ?? undefined,
		discountPercentage: row.discountPercentage,
		totalPrice: row.totalPrice,
		itemId: row.xeroItemId,
		itemCode: row.xeroItemCode,
		accountId: row.xeroAccount,
		accountCode: row.accountCode,
		/*
		 * The shared "apply to all lines" dropdown writes only `tax`, through
		 * mapInvoiceEntryToRow; the line's own Account column writes `xeroTaxRate`. The column
		 * has to render whichever applies, or picking from the shared dropdown leaves the Tax
		 * cell empty while the summary shows tax - the three numbers disagreeing that R1 exists
		 * to prevent. `??`, not `||`: zero is a real rate a line can be deliberately set to.
		 */
		taxRate: row.xeroTaxRate ?? row.tax ?? undefined,
		taxType: row.xeroTaxType,
		tracking: row.xeroTracking,
	}));

/**
 * The three fields the invoice's money derives from, recomputed for a newly chosen account.
 *
 * `Util.getInvoiceTotals` - which feeds the modal's Subtotal / Discount / Tax / Total - reduces
 * over `tax` and `discount` as PERCENTAGES, and `mapInvoiceEntryToRow` is the only thing that
 * writes `tax`, `taxPercentage` and `totalPrice` today, from the shared account dropdown. An
 * account chosen in the line's own column has to write the same three, or the Tax column, the
 * Total column and the summary each state a different number for one line.
 *
 * An absent rate becomes 0 here, matching `xeroAccount?.taxRate || 0`: an account carrying no
 * rate still puts no tax on the line. `xeroTaxRate` keeps the same number rather than a separate
 * "no rate" state - the Tax column reads `xeroTaxRate ?? tax` while the line total and the
 * summary read `tax`, so the two must hold the same value or they state different things.
 */
const derivedMoneyFields = (row: InvoiceEntryRow, taxRate: number | undefined, unitPrice: number | null | undefined) => {
	const rate = taxRate ?? 0;
	return {
		tax: rate,
		taxPercentage: rate / 100,
		/*
		 * The price is a PARAMETER, not `row.unitPrice`: picking an item now moves the price as
		 * well as the account, and deriving the total from the price being replaced would leave
		 * the Unit Price column and the Total column stating different things.
		 */
		totalPrice: Util.getInvoiceEntryAmount(unitPrice, row.quantity, row.discount, rate),
	};
};

/**
 * The selections coming back, applied onto the modal's own rows.
 *
 * Merged rather than replaced: the table's rows carry only what it renders, and everything else
 * on an `InvoiceEntryRow` - the Relay fields, the QuickBooks and e-conomic selections - has to
 * survive a change made in a Xero column.
 *
 * **It writes `tax`, `taxPercentage` and `totalPrice` as well as the selections**, through
 * `derivedMoneyFields`. It did not in T4-132, and the omission was deliberate there: the Tax
 * column and the modal's summary totals are computed from those three, so a selection that moved
 * the ids without moving the money would leave the cell and the total stating different numbers.
 * Nothing rendered the mapping then, so nothing could see the gap. T4-133 renders it and closes
 * it here. `label` is still not written - it is derived at render time from the options list.
 */
export const applyXeroLineRows = (rows: InvoiceEntryRow[], changed: XeroExportLineRow[]): InvoiceEntryRow[] =>
	rows.map(row => {
		const update = changed.find(candidate => candidate.id === row.id);
		if (!update) return row;

		/*
		 * `??`, not the update alone: `toXeroLineRows` maps a null price to undefined, so a line
		 * that never carried a price would otherwise have its null replaced by undefined on any
		 * unrelated edit.
		 */
		const unitPrice = update.unitPrice ?? row.unitPrice;

		return {
			...row,
			xeroItemId: update.itemId,
			xeroItemCode: update.itemCode,
			xeroItemDescription: update.itemDescription,
			xeroAccount: update.accountId,
			accountCode: update.accountCode,
			xeroTaxRate: update.taxRate,
			xeroTaxType: update.taxType,
			xeroTracking: update.tracking,
			unitPrice,
			...derivedMoneyFields(row, update.taxRate, unitPrice),
		};
	});

/**
 * The stored configuration, as service-company's REST models serialise it.
 *
 * Deliberately loose: these cross a network boundary, and the one defect this epic has already
 * shipped came from trusting a declared type over the wire (Xero sent `EffectiveRate` as a JSON
 * number while the interface said string, and `.trim()` broke every import).
 */
type StoredAccount = {externalId?: string; code?: string; name?: string; taxType?: string; defaultTaxRate?: unknown};
type StoredItem = {
	externalId?: string;
	code?: string;
	name?: string;
	defaultAccountCode?: string | null;
	defaultUnitPrice?: unknown;
	description?: string | null;
};
type StoredTrackingOption = {externalId?: string; name?: string};
type StoredTrackingCategory = {externalId?: string; name?: string; options?: StoredTrackingOption[] | null};

/** A 403 sends no array, and an error body is an object; either must render as "none", not crash. */
const asList = <T>(payload: unknown): T[] => (Array.isArray(payload) ? (payload as T[]) : []);

/**
 * A Java BigDecimal as it reaches the browser: whether `defaultTaxRate` arrives as 20 or "20", and
 * `defaultUnitPrice` as 60 or "60.0000", is the serialiser's choice. Absent stays null rather than
 * becoming 0, for both - an account with no rate is not an account rated at 0%, and an item Xero
 * holds no price for is not an item priced at nothing. Only one of each pair is safe on a line.
 */
const asDecimal = (value: unknown): number | null => {
	if (value === null || value === undefined || value === '') return null;
	const rate = Number(value);
	return Number.isFinite(rate) ? rate : null;
};

export const toAccountOptions = (payload: unknown): XeroAccountOption[] =>
	asList<StoredAccount>(payload).map(account => ({
		value: account.externalId ?? '',
		code: account.code ?? '',
		name: account.name ?? '',
		taxRate: asDecimal(account.defaultTaxRate),
		/*
		 * The rate is what the line displays; the type is what the export sends - and the type is
		 * withheld exactly when the rate is, because the two describe the same thing.
		 *
		 * IntegrationGlAccountRepository joins the rate on INTEGRATION_TAX_RATE.STATUS = ACTIVE, so
		 * an account whose tax rate was archived or deleted in Xero comes back with no rate at all -
		 * deliberately, because a line carrying such a rate is the export rejection this epic exists
		 * to remove. `taxType` is returned regardless, straight off the account row. Sending it would
		 * put the tax type whose rate the backend just refused to offer onto the outbound line,
		 * turning an export that worked before this story into one Xero rejects.
		 *
		 * Absent, not zero: asDecimal maps only null/undefined/'' to null, so an account deliberately
		 * rated at 0 keeps its type.
		 */
		taxType: asDecimal(account.defaultTaxRate) === null ? undefined : account.taxType,
	}));

/**
 * Xero requires only ONE of Code and Name on an item, so either can be absent. The dropdown shows
 * the name; falling back to the code keeps a code-only item pickable instead of rendering a blank
 * row nobody can identify.
 *
 * An item with NO code is excluded, not relabelled. `itemCode` is the only field that carries an
 * item to the xero service - `XeroAccountMapEntry` has no `itemId` and neither does
 * `XeroAccountLink` in the graphql schema - so a name-only item was selectable, rendered its name
 * in the cell, and then had its empty `itemCode` dropped from the payload as falsy. The export
 * reported success with no item on the line and nothing on screen said so.
 *
 * Excluding it is the smaller of the two honest fixes. The other is to add `itemId` to
 * `XeroAccountLink` and resolve it server-side, which is what `XeroLineTracking` already does and
 * is the better end state - but it is a schema change across three repos and belongs in its own
 * ticket. Offering a choice that cannot be sent is worse than not offering it.
 */
export const toItemOptions = (payload: unknown): XeroItemOption[] =>
	asList<StoredItem>(payload)
		.filter(item => !!item.code)
		.map(item => ({
			value: item.externalId ?? '',
			code: item.code ?? '',
			name: item.name || item.code || '',
			/*
			 * Xero's own default account for this item, as a CODE. Resolved against the loaded
			 * account list at selection time rather than here, because products and accounts
			 * import under separate buttons in either order - an item can name an account this
			 * company has not imported, and that has to degrade to "no account" rather than break.
			 *
			 * `defaultTaxType` is imported but deliberately not carried: the tax comes from the
			 * resolved ACCOUNT, so the rate the line displays and the TaxType the export sends
			 * cannot disagree.
			 */
			defaultAccountCode: item.defaultAccountCode ?? undefined,
			/*
			 * Xero's SalesDetails.UnitPrice. `?? undefined` rather than keeping the null, because
			 * this is spread onto the line: a null would blank a price the user had, where an
			 * absent key leaves it alone. A real 0 survives, and is meant to.
			 */
			defaultUnitPrice: asDecimal(item.defaultUnitPrice) ?? undefined,
			description: item.description?.trim() || undefined,
		}));

export const toTrackingCategories = (payload: unknown): XeroTrackingCategory[] =>
	asList<StoredTrackingCategory>(payload).map(category => ({
		value: category.externalId ?? '',
		name: category.name ?? '',
		// A category whose options were all archived in Xero still comes back, with none.
		options: asList<StoredTrackingOption>(category.options).map(option => ({
			value: option.externalId ?? '',
			name: option.name ?? '',
		})),
	}));

export type XeroLineConfig = {
	accounts: XeroAccountOption[];
	items: XeroItemOption[];
	trackingCategories: XeroTrackingCategory[];
	/**
	 * Which reads failed, as opposed to genuinely returning nothing.
	 *
	 * AC3 asks for the empty state to be driven by the real "nothing imported" signal, and a
	 * failed read is not that signal: without this, a 500 on `integration_gl_account` tells a
	 * company with 300 imported accounts that none exist, disables Export, and sends them to
	 * re-import what is already there.
	 *
	 * Per read rather than one flag for all three. A shared flag is wrong in both directions: it
	 * labels a column whose read SUCCEEDED as failed - 300 accounts reading "Could not load"
	 * because the tracking read 500'd - and it overrides a genuine "nothing imported" on a type
	 * that answered perfectly well.
	 */
	failed: XeroLineConfigFailures;
};

export type XeroLineConfigFailures = {
	accounts: boolean;
	items: boolean;
	trackingCategories: boolean;
};

export const EMPTY_LINE_CONFIG: XeroLineConfig = {
	accounts: [],
	items: [],
	trackingCategories: [],
	failed: {accounts: false, items: false, trackingCategories: false},
};

/** No configuration, because it could not be read at all: every column shows its failed state. */
export const FAILED_LINE_CONFIG: XeroLineConfig = {
	accounts: [],
	items: [],
	trackingCategories: [],
	failed: {accounts: true, items: true, trackingCategories: true},
};

/**
 * One configuration read, which never rejects.
 *
 * A 403 is an unconnected or unauthorised organisation. Anything else that is not a 2xx - a 500,
 * or the flag gate's own empty-bodied 404, reachable whenever web-front's flag evaluation and
 * graphql's disagree - has a body that is not JSON, so `.json()` rejects. Left unhandled that
 * rejection took the whole `Promise.all` with it and the configuration stayed empty *forever*:
 * the user reads "No accounts imported" on a company with hundreds of them, and the Export button
 * never enables, with an unhandled rejection in the console as the only clue.
 *
 * Resolving instead of rejecting means one failed read leaves the other two alone, and the
 * failure is reported against that read so the column can say so rather than claiming nothing was
 * imported.
 */
type ConfigRead = {payload: unknown; failed: boolean};

const readConfig = async (path: string): Promise<ConfigRead> => {
	try {
		const response = await DirectApi.Fetch_Raw(path);
		// A 403 is an unconnected or unauthorised organisation - a real answer, not a failure.
		if (response.status === 403) {
			return {payload: null, failed: false};
		}
		if (!response.ok) {
			return {payload: null, failed: true};
		}
		return {payload: await response.json(), failed: false};
	} catch (e) {
		return {payload: null, failed: true};
	}
};

/** What `fetchXeroLineConfig` already read for this organisation, or undefined. */
export const cachedXeroLineConfig = (tenantId: string | undefined): XeroLineConfig | undefined =>
	tenantId ? CONFIG_CACHE.get(tenantId) : undefined;

export {clearXeroLineConfigCache};

/**
 * The three reads themselves. Split out so `fetchXeroLineConfig` can hold the promise of this
 * before any of them resolve, which is what stops N invoice rows each starting their own set.
 */
const readLineConfig = async (tenantId: string): Promise<XeroLineConfig> => {
	const [accounts, items, trackingCategories] = await Promise.all([
		readConfig(`xero/configuration/accounts/${tenantId}`),
		readConfig(`xero/configuration/products/${tenantId}`),
		readConfig(`xero/configuration/tracking-categories/${tenantId}`),
	]);

	const config = {
		accounts: toAccountOptions(accounts.payload),
		items: toItemOptions(items.payload),
		trackingCategories: toTrackingCategories(trackingCategories.payload),
		failed: {
			accounts: accounts.failed,
			items: items.failed,
			trackingCategories: trackingCategories.failed,
		},
	};

	/*
	 * Only a clean read is cached. Caching a failed one would pin "Could not load" for the rest of
	 * the session, and the user's only way out would be a page reload - worse than the load they
	 * were waiting on.
	 */
	if (!accounts.failed && !items.failed && !trackingCategories.failed) {
		CONFIG_CACHE.set(tenantId, config);
	}

	return config;
};

/**
 * All three reads together, so the table never renders with one dropdown populated and another
 * still empty - which would read as "nothing imported" rather than "still loading".
 */
export const fetchXeroLineConfig = (tenantId: string | undefined): Promise<XeroLineConfig> => {
	if (!tenantId) return Promise.resolve(EMPTY_LINE_CONFIG);

	const alreadyRead = CONFIG_CACHE.get(tenantId);
	if (alreadyRead) return Promise.resolve(alreadyRead);

	/*
	 * Join a read already in flight rather than starting a second one.
	 *
	 * The result cache cannot do this job: it is written only after all three reads resolve, so
	 * every caller that arrives in the meantime finds it empty. The Export button warms this once
	 * per invoice ROW, and all of those resume together when the shared contacts promise settles -
	 * so a 25-row list made 76 requests where this module's own comment promised 4.
	 */
	const inFlight = CONFIG_IN_FLIGHT.get(tenantId);
	if (inFlight) return inFlight;

	/*
	 * The cleanup removes only ITS OWN entry. `clearXeroLineConfigCache` - called by
	 * `XeroConfigurationImport` once an admin finishes importing, so the next modal does not serve the
	 * configuration from before the import - empties this map while a read may still be running, so a
	 * later caller starts a replacement under the same key. Deleting unconditionally took that
	 * REPLACEMENT out of the map when the orphan finally settled, and the next caller started a third
	 * set of reads for an organisation already being read.
	 */
	const read: Promise<XeroLineConfig> = readLineConfig(tenantId).finally(() => {
		if (CONFIG_IN_FLIGHT.get(tenantId) === read) CONFIG_IN_FLIGHT.delete(tenantId);
	});
	CONFIG_IN_FLIGHT.set(tenantId, read);
	return read;
};

/**
 * Warm the cache without rendering anything, so the modal opens populated.
 *
 * Deliberately fire-and-forget: a prefetch that fails must not surface anywhere, because the modal
 * reads the same endpoints again and reports failure itself. Skipped when already cached, so
 * mounting a page twice does not re-read.
 */
export const prefetchXeroLineConfig = (tenantId: string | undefined): void => {
	if (!tenantId || CONFIG_CACHE.has(tenantId)) return;
	void fetchXeroLineConfig(tenantId).catch(() => undefined);
};

/**
 * One `xero/contacts` read, shared. The organisation a line's configuration belongs to is only
 * discoverable through a contact, so a prefetch has to make this call first - and the export
 * button renders once per invoice row, so without memoising it the page would make one per row.
 *
 * The promise is held, not the result: rows mount in the same tick, and caching only on resolve
 * would let every one of them start its own request before the first came back.
 */

const readXeroContactsOnce = (): Promise<{ContactID?: string; TenantId?: string}[] | null> => {
	const inFlight = getContactsRead();
	if (inFlight) return inFlight;

	const read = DirectApi.Fetch_Raw('xero/contacts')
		.then((result: Response) => (result.status === 200 ? result.json() : null))
		.catch(() => null);

	setContactsRead(read);
	return read;
};

/**
 * Warm every organisation this company can export to, so the modal opens populated on the FIRST
 * click rather than the second.
 *
 * All organisations rather than the invoice's own: the export button knows the company's clients
 * but not which of them this invoice belongs to - that is resolved inside the modal. A company has
 * as many organisations as it has Xero connections, which is one or two, and `prefetchXeroLineConfig`
 * skips any already read.
 */
export const prefetchXeroLineConfigForCompany = async (): Promise<void> => {
	const contacts = await readXeroContactsOnce();
	if (!Array.isArray(contacts)) return;

	new Set(contacts.map(contact => contact.TenantId).filter(Boolean)).forEach(tenantId => prefetchXeroLineConfig(tenantId));
};

/**
 * What picking a Product/Service should put on the line.
 *
 * The item alone is the user's choice; the account is Xero's own default for it, and the tax
 * follows from THAT account rather than from the item's own `defaultTaxType`.
 *
 * Not because Xero would derive it anyway - it would not. `invoiceXeroLogic.appendLine` sends an
 * explicit `TaxType` on every line, so Xero books whatever this modal puts there. The reason is
 * simpler: the modal fills the line's TaxType from the resolved ACCOUNT, and that is the value the
 * export sends, so the rate the user reads and the rate Xero books cannot disagree. Filling it from
 * the item instead would display the account's rate and export the item's type, which is exactly
 * the cell-versus-total contradiction the rest of this file exists to prevent.
 *
 * The account is a suggestion, not a lock: it lands in the same fields the Account column writes,
 * so the next pick from that column replaces it exactly as if nothing had been suggested.
 *
 * **The price is the item's own**, from Xero's `SalesDetails.UnitPrice`, and is filled independently
 * of the account - the two arrive in different Xero fields, so an item whose account this company
 * never imported still fills its price.
 *
 * **Known limitation, accepted deliberately (Pritesh, 2026-09-30):** Xero's `UnitPrice` carries no
 * currency and is always in the ORGANISATION's base currency, which Forecast does not store - there
 * is no currency column on `bill_from`. On an invoice drawn in another currency the filled figure
 * is therefore wrong until someone corrects it: a USD invoice from a GBP organisation gets the GBP
 * number with a dollar sign on it. It is filled anyway because the user reads the line before
 * exporting and can change it, and because leaving the field empty every time is the worse trade
 * for the many companies that invoice only in their base currency. Importing the organisation's
 * base currency and filling the price only when it matches the invoice's is the real fix.
 */
/**
 * Whether the account on a line was put there by the line's item, rather than chosen by the user.
 *
 * DERIVED from the two fields the row already carries, not recorded when the account is written. A
 * recorded flag would have to survive `toXeroLineRows`/`applyXeroLineRows` and the legacy pair
 * beside them - four functions, and a miss in any one of them reverts silently - and would still be
 * wrong after the modal is closed and reopened, because the row is then rebuilt from the invoice
 * entry with no memory of who chose what. The comparison below is right in both cases.
 *
 * It cannot tell an account the item filled from the same account picked by hand, and does not try:
 * the two hold the identical code, so clearing either leaves the line in the same place.
 */
export const accountCameFromItem = (row: XeroExportLineRow, items: XeroItemOption[]): boolean => {
	if (!row.itemId || !row.accountCode) return false;

	const item = items.find(candidate => candidate.value === row.itemId);

	/*
	 * `?.` carries two cases to the same answer: an item no longer in the list - deleted in Xero and
	 * re-imported since - and an item that names no default account. Neither can be shown to have
	 * filled this line, so the account stays. Both compare undefined against a code the guard above
	 * has already established is non-empty, so neither can match.
	 */
	return item?.defaultAccountCode === row.accountCode;
};

export const applyItemDefaults = (
	item: XeroItemOption | undefined,
	accounts: XeroAccountOption[],
	/**
	 * The price the invoice entry itself carries, which is what an item with no price of its own
	 * restores.
	 *
	 * The line's CURRENT price cannot serve: once a product has filled it, a line originally at
	 * 500 reads 60, so "leave what is there" keeps 60 when the user clears the product or picks
	 * one Xero holds no price for - and the export then writes 60 to the invoice, with Unit Price
	 * read-only and no undo but closing the modal.
	 */
	originalUnitPrice?: number,
	/**
	 * Whether the account currently on the line was put there by an item - `accountCameFromItem`.
	 *
	 * Decides what clearing the item clears. The item's own account goes with it - otherwise the line
	 * keeps claiming an account no product on it chose, which is what testv2 reported. An account the
	 * USER chose stays: opening this dropdown and picking None is a no-op in their mind.
	 */
	accountFromItem = false
): XeroLineSelection => {
	/*
	 * Keys present, not merely undefined. This object is SPREAD onto the row, so an absent key leaves
	 * the row's account exactly where it was - which was the bug.
	 */
	const clearedAccount = {
		accountId: undefined,
		accountCode: undefined,
		taxRate: undefined,
		taxType: undefined,
	};

	if (!item) {
		return {
			itemId: undefined,
			itemCode: undefined,
			itemDescription: undefined,
			unitPrice: originalUnitPrice,
			...(accountFromItem ? clearedAccount : {}),
		};
	}

	const selection: XeroLineSelection = {
		itemId: item.value,
		itemCode: item.code,
		// Key present even without a description, so the previous item's does not outlive it.
		itemDescription: item.description || undefined,
		// The item's own price, or the entry's back again. Never the price a previous item left.
		unitPrice: item.defaultUnitPrice ?? originalUnitPrice,
	};

	const account = item.defaultAccountCode
		? accounts.find(candidate => candidate.code === item.defaultAccountCode)
		: undefined;

	/*
	 * An item that supplies no account - or names one this company never imported - still clears the
	 * PREVIOUS item's account, for the same reason None does. It leaves the user's own alone.
	 */
	if (!account) return accountFromItem ? {...selection, ...clearedAccount} : selection;

	return {
		...selection,
		accountId: account.value,
		accountCode: account.code,
		// null means the account carries no rate; undefined keeps that distinct from a real zero.
		taxRate: account.taxRate ?? undefined,
		taxType: account.taxType,
	};
};

/**
 * The line-level Xero selections, and the QuickBooks line class, carried across a rebuild of the rows.
 *
 * The modal rebuilds every row from `initialInvoiceEntries` whenever a shared "apply to all
 * lines" dropdown changes: the Xero account, or the QuickBooks product/service. Those are the raw
 * Relay entries, so `xeroItemId`, `xeroItemCode`, `xeroTracking` and `quickbooksClass` - which exist
 * only on the modal's own rows - are dropped: fill Item and Region (or Class) on twenty lines, touch
 * that dropdown, lose all twenty, with no warning and no undo.
 *
 * The account and its tax are deliberately NOT carried. Choosing from a control labelled "apply
 * to all lines" is an instruction to overwrite them, and honouring it is the point of the control.
 *
 * Safe for a flag-off company by construction: these fields are written solely by the new
 * tables, so for such a row this copies `undefined` onto `undefined` and changes nothing.
 */
export const carryXeroSelections = (rebuilt: InvoiceEntryRow[], previous: InvoiceEntryRow[]): InvoiceEntryRow[] =>
	rebuilt.map(row => {
		const carried = previous.find(candidate => candidate.id === row.id);
		return carried
			? {
					...row,
					xeroItemId: carried.xeroItemId,
					xeroItemCode: carried.xeroItemCode,
					xeroItemDescription: carried.xeroItemDescription,
					xeroTracking: carried.xeroTracking,
					quickbooksClass: carried.quickbooksClass,
			  }
			: row;
	});

/** The Xero contact the client is linked to. `Array.isArray`-guarded, for the error body described on `selectedTenantFor`. */
export const xeroContactFor = <Contact extends {ContactID?: string}>(
	contacts: Contact[] | null | undefined,
	client: {xeroContactId?: string} | null | undefined
): Contact | undefined =>
	Array.isArray(contacts) ? contacts.find(contact => contact.ContactID === client?.xeroContactId) : undefined;

/**
 * The organisation the selected client resolves to, from whatever `xero/contacts` returned.
 *
 * `Array.isArray` rather than `?.`, because the failure case is not nullish. `getXeroContacts`
 * bails only on a 403; on any other non-OK status it parses the body and stores it, and the xero
 * service answers a failure with `messageResponse(500, ...)` - so the state becomes
 * `{message: '...'}`. A `.find` on that throws a TypeError, and because this is read during render
 * that is an ErrorBoundary crash rather than the caught throw and clean XERO_INACCESSIBLE modal
 * the same state used to produce. `xeroClientsSpliced` in the same modal already guards the same state the same way.
 *
 * Computed unconditionally rather than only under the flag, because that is simpler than gating
 * it and the guard above makes it harmless. On this branch only `isConfigStaleForClient` consumes
 * it, inside the flag check, so gating would change nothing; T4-134 consumes it unconditionally in
 * its readiness predicate, where returning `undefined` flag-off WOULD block every export.
 */
export const selectedTenantFor = (
	xeroContacts: {ContactID?: string; TenantId?: string}[] | undefined,
	selectedClient: {xeroContactId?: string} | undefined
): string | undefined => xeroContactFor(xeroContacts, selectedClient)?.TenantId;

/** One entry of the `xero/contacts` answer, as far as the export window reads it. */
export type XeroContact = {ContactID?: string; TenantId?: string; Organization?: string; DefaultTaxRate?: number | null};

/**
 * What the legacy-path window's `xero/contacts` read produced.
 *
 * - the contacts, when the answer was OK; an OK answer that is not a list is no contacts.
 * - `'inaccessible'` on a 403: Xero is not reachable for this company, and the window closes for the
 *   inaccessible one, as the legacy window does.
 * - `'failed'` for any other error status, a body that is not JSON, or a read that rejects: the
 *   window stays open, shows its lists as failed to load, and keeps Export blocked.
 */
export type XeroContactsResult = XeroContact[] | 'inaccessible' | 'failed';

/**
 * The legacy-path window's `xero/contacts` read: a fresh one on every open, not `readXeroContactsOnce`,
 * which is held for the life of the page - a contact linked since the page loaded must be seen.
 */
export const fetchXeroContacts = (): Promise<XeroContactsResult> =>
	DirectApi.Fetch_Raw('xero/contacts')
		.then(async (result: Response): Promise<XeroContactsResult> => {
			if (result.status === 403) return 'inaccessible';
			if (!result.ok) return 'failed';
			const body = await result.json();
			return Array.isArray(body) ? (body as XeroContact[]) : [];
		})
		.catch((): XeroContactsResult => 'failed');

/**
 * Whether the loaded configuration belongs to an organisation the user is no longer on.
 *
 * `hasUnresolvableAccount` asks whether the ids resolve against the list that is loaded. That is
 * not sufficient on its own, because the loaded list can itself be the wrong one: the mount effect
 * has `[]` deps, so its `selectedClient` is the FIRST render's, and the Customer dropdown is
 * usable while `xero/contacts` is still in flight. Switch client in that window and the effect
 * still loads the original client's organisation, which passes the in-flight guard because
 * `requestedTenant` was set to it. The accounts on screen are A's, the selected client is B, and
 * every id resolves - against A.
 *
 * `null` and `undefined` both mean "no organisation": the ref starts at `null` and the contact
 * lookup returns `undefined`, and a raw `!==` would report those two as a mismatch.
 */
export const isConfigStaleForClient = (
	loadedTenantId: string | null | undefined,
	selectedTenantId: string | null | undefined
): boolean => (loadedTenantId ?? null) !== (selectedTenantId ?? null);

/**
 * Whether any line holds an account the loaded organisation cannot account for.
 *
 * Switching client reloads the dropdown lists for the new tenant but leaves the per-line
 * selections alone, and Export is gated on "every line has an `xeroAccount`" - which stays true,
 * because the ids are still sitting there. The Account cell falls back to its placeholder, since
 * the old id is not in the new list, but the mutation still posts the old `accountCode`, and the
 * xero service builds `LineItem.AccountCode` from that code alone. A common code like "200"
 * therefore lands silently on a different account in organisation B.
 *
 * The tenant stamp guards which LISTS are shown, not which selections outlive them. This closes
 * the gap from the other end: a selection the loaded list cannot account for blocks the export
 * rather than travelling as a bare code. T4-134 closes it from the first end too, by clearing the
 * selections on an organisation change - the two compose, and neither makes the other redundant.
 *
 * An empty list is NOT exempt, and the first version of this got that wrong. It returned early on
 * `accounts.length === 0`, reasoning that an empty list meant "not loaded yet" and that the
 * loading state would gate it. Nothing gated it - at the time `xeroLineConfigLoading` reached the
 * table and never the Export button, which is no longer true, because the fix wired it there - so
 * the exemption reopened the original bug in the three cases where the list
 * is legitimately empty while the lines still hold the previous organisation's ids: the loading
 * window (`loadXeroLineConfig` sets `EMPTY_LINE_CONFIG` before the request even goes out, so this
 * is every organisation switch rather than an edge case), an organisation that has not run the
 * configuration import, and a failed read (`toAccountOptions(null)` is `[]` too).
 *
 * An empty list resolves nothing, so a line holding an account against it is precisely this
 * predicate's business. First render is safe without the exemption because, under the flag, a
 * fresh line carries no `xeroAccount` at all - the invoice-level dropdown that used to set one is
 * hidden - and the caller pairs this with `xeroLineConfigLoading`.
 */
export const hasUnresolvableAccount = (lines: {xeroAccount?: string}[], accounts: {value: string}[]): boolean =>
	lines.some(line => !!line.xeroAccount && !accounts.some(account => account.value === line.xeroAccount));

/**
 * A Xero contact's default tax rate, applied to each line that has not chosen its own. Returns new
 * rows instead of writing to them; a line the rule does not touch is returned as the same object.
 *
 * The precedence rule, in one place because the export windows disagreed about it: the line's
 * selected account drives its tax, so the contact's rate is a default that applies **until an
 * account is chosen**, and never overwrites one.
 *
 * The rate, `xeroTaxRate`, percentage and total are written together. The Tax column reads
 * `xeroTaxRate ?? tax` while the line total and the invoice summary read `tax`, so writing one
 * without the other leaves the column saying 20% while the total says nothing.
 */
export const withContactTaxRate = (rows: InvoiceEntryRow[], contactRate: number | null | undefined): InvoiceEntryRow[] => {
	/*
	 * No rate is not a rate of zero. Writing `undefined` makes Util.getInvoiceTotals compute
	 * `(undefined / 100) * grossAmount`, so the Tax and Total read NaN and the line's own stored
	 * rate is destroyed. The rate is absent whenever the Xero contact has no
	 * `AccountsReceivableTaxType` or its type matches no tax rate, and always on the no-contact
	 * path. `== null` rather than falsy, because 0 is a real rate a line can legitimately carry.
	 */
	if (contactRate == null) return rows;
	return rows.map(row =>
		row.xeroAccount ? row : {...row, xeroTaxRate: contactRate, ...derivedMoneyFields(row, contactRate, row.unitPrice)}
	);
};

/**
 * `withContactTaxRate` for callers that write to their line objects in place, as the flag-on branch
 * of the older Xero window does. The rule itself lives in `withContactTaxRate`; this only copies its
 * answer onto the line, and leaves the line untouched when the rule changes nothing.
 */
export const applyContactTaxRate = (
	line: {xeroAccount?: string; tax?: number; xeroTaxRate?: number},
	contactRate: number | null | undefined
): void => {
	const [updated] = withContactTaxRate([line as InvoiceEntryRow], contactRate);
	if (updated !== line) Object.assign(line, updated);
};

/**
 * The Client options with each one's Xero organisation after its name, as the old window showed
 * them: two clients of the same name in different organisations must be told apart.
 *
 * A client whose contact is not found, or names no organisation, keeps its plain name. The list
 * itself is returned unchanged until the contacts have answered.
 */
export const withOrganisationLabels = (clients: XeroClientOption[], contacts: XeroContact[] | null): XeroClientOption[] => {
	if (!contacts || contacts.length === 0) return clients;
	return clients.map(client => {
		const organisation = xeroContactFor(contacts, client)?.Organization;
		return organisation ? {...client, label: `${client.label} (${organisation})`} : client;
	});
};

/**
 * Each line's tax percentage and total worked out again from the `tax` and `unitPrice` it now holds.
 *
 * For lines `clearXeroLineSelections` has just cleared: it puts back the entry's own price and a
 * new tax, but the new window's Total cell shows the stored `totalPrice`, which would still state
 * the cleared item's price and the old account's tax.
 */
export const withRestatedMoney = (rows: InvoiceEntryRow[]): InvoiceEntryRow[] =>
	rows.map(row => ({...row, ...derivedMoneyFields(row, row.tax ?? undefined, row.unitPrice)}));

/** One entry of the export mutation's accountMap. */
export type XeroAccountMapEntry = {
	entryId: string;
	accountId?: string;
	accountCode?: string;
	itemCode?: string;
	/** Present only when the picked item's description differs from the line's own. */
	description?: string;
	taxType?: string;
	tracking?: {trackingCategoryId: string; trackingOptionId: string}[];
	/** Present only for a line the user repriced; the mutation writes it to the invoice entry. */
	unitPrice?: number;
};

/**
 * The export mutation's `accountMap`, built from the modal's rows.
 *
 * Every field past `accountCode` is OMITTED when absent rather than sent as `undefined`. AC1 asks
 * for a payload identical to today's, and a row carrying no selections has to produce exactly the
 * three-field entry the xero service built lines from before.
 *
 * A tracking category the user left blank is simply not a key in `xeroTracking`, so it produces
 * no entry - which is where AC3 begins, before the xero service ever sees the body. A category
 * the user opened and left on the blank option IS a key, with an empty value, which the filter
 * below drops.
 *
 * The parameter is structural rather than `InvoiceEntryRow[]`, because both modals call this:
 * the TS modal with `InvoiceEntryRow`, the legacy one with `LegacyInvoiceLine`. It worked only
 * because the read fields overlap and JS checks nothing at that boundary. `toPreFlagAccountMap`
 * already took the honest shape; this now matches it.
 */
export const toXeroAccountMap = (
	rows: {
		id: string;
		xeroAccount?: string;
		accountCode?: string;
		xeroItemCode?: string;
		entryDescription?: string | null;
		xeroItemDescription?: string;
		xeroTaxType?: string;
		xeroTracking?: Record<string, string> | null;
	}[],
	/**
	 * The lines whose price picking a product changed, from `changedUnitPrices`.
	 *
	 * Sent on the map so the xero service can send each as the line's `UnitAmount` and write it back
	 * to the invoice entry once Xero has accepted - one call with one outcome, rather than the
	 * browser fanning out a mutation per line and having to decide what to do when some succeed and
	 * some fail.
	 *
	 * Defaults to none, so every caller that changed no price - including the whole flag-off path -
	 * produces exactly the body it produced before.
	 */
	repriced: {id: string; unitPrice: number}[] = []
): XeroAccountMapEntry[] =>
	rows.map(row => {
		const entry: XeroAccountMapEntry = {
			entryId: row.id,
			accountId: row.xeroAccount,
			accountCode: row.accountCode,
		};

		if (row.xeroItemCode) entry.itemCode = row.xeroItemCode;
		if (row.xeroTaxType) entry.taxType = row.xeroTaxType;
		if (row.xeroItemDescription && row.xeroItemDescription !== row.entryDescription) {
			entry.description = row.xeroItemDescription;
		}

		/*
		 * `find` then an explicit key, not `?? undefined`: a line nobody repriced must carry no
		 * `unitPrice` key at all, or the xero service would rewrite every entry on the invoice. A
		 * price of 0 is a real price and survives, which a truthiness check would drop.
		 */
		const priceChange = repriced.find(change => change.id === row.id);
		if (priceChange) entry.unitPrice = priceChange.unitPrice;

		const tracking = Object.entries(row.xeroTracking ?? {})
			.filter(([, optionId]) => !!optionId)
			.map(([trackingCategoryId, trackingOptionId]) => ({trackingCategoryId, trackingOptionId}));
		if (tracking.length > 0) entry.tracking = tracking;

		return entry;
	});

/**
 * Every Xero classification dropped from the lines, for when the organisation changes underneath.
 *
 * The legacy modal lets the user switch client mid-flow, and a different client can belong to a
 * different Xero organisation. Reloading the dropdowns is not enough: the ids already on the rows
 * belong to the previous one. The cells render their placeholder, because those ids are not in the
 * new lists - but `!line.xeroAccount` still passes, so Export stays enabled and the previous
 * organisation's tracking option ids, tax type and item code go to Xero.
 *
 * Cleared rather than filtered on the way out: filtering would silently drop what the user
 * believed they had chosen, while clearing makes the cells visibly empty and blocks Export until
 * they choose again in the organisation they are now exporting to.
 *
 * Constrained only to `object`, and it reads nothing: both modals' line shapes pass through it,
 * and every field it writes is optional on each.
 */
export const clearXeroLineSelections = <T extends object>(lines: T[], contactTaxRate?: number): T[] =>
	lines.map(line => ({
		...line,
		xeroAccount: undefined,
		accountCode: undefined,
		xeroItemId: undefined,
		xeroItemCode: undefined,
		xeroItemDescription: undefined,
		xeroTaxRate: undefined,
		xeroTaxType: undefined,
		xeroTracking: undefined,
		/*
		 * The price the cleared product put there goes with it, back to the entry's own.
		 *
		 * Clearing the item but keeping its price is worse than keeping both: the line reads 60
		 * with no product to explain it, the user has no way to edit it back - Unit Price is
		 * read-only - and the export sends that 60 for Xero to record. Found in review by switching
		 * to a client in another organisation after picking a product priced 60.
		 *
		 * `??` so a line that never carried an original is left exactly as it was rather than
		 * blanked.
		 */
		unitPrice:
			(line as {originalUnitPrice?: number | null}).originalUnitPrice ?? (line as {unitPrice?: number | null}).unitPrice,
		/*
		 * `tax` too. The Tax cell reads `xeroTaxRate ?? tax` and `Util.getInvoiceTotals` reduces
		 * over `tax`, so leaving it behind would show the previous organisation's rate both in the
		 * cell and in the invoice total, against an account the user can no longer see.
		 *
		 * It falls back to the NEW contact's default rather than to zero. Zeroing was its own
		 * quiet misstatement: the line is still going to be taxed when it reaches Xero, and an
		 * invoice total that reads as tax-free because the user switched organisation is a number
		 * someone could send to a client.
		 */
		tax: contactTaxRate ?? 0,
	})) as T[];

/**
 * Whether the organisation actually changed, and the line selections are therefore stale.
 *
 * Separate from the clearing so the decision itself is testable: inverting it clears on every
 * load, wiping selections the user has just made, and the clearing function's own tests would not
 * notice either way.
 *
 * A first load has no previous organisation, and a client with no Xero contact resolves none - in
 * both cases there is nothing to invalidate.
 */
export const shouldClearOnTenantChange = (
	previousTenantId: string | null | undefined,
	nextTenantId: string | null | undefined
): boolean => !!previousTenantId && !!nextTenantId && previousTenantId !== nextTenantId;

/**
 * One line as the legacy modal holds it.
 *
 * `xero_export_modal.js` is untyped JS and builds these in `getInitialInvoiceLines` by spreading a
 * Relay node, so this describes only what the two helpers below touch. Indexable, because the rest
 * of the object travels through the merge untouched and must not be lost.
 */
export type LegacyInvoiceLine = {
	id: string;
	description?: string | null;
	project?: {companyProjectId?: number | null; name?: string | null; id?: string; projectColor?: string | null} | null;
	/*
	 * A STRING, not a number: getInitialInvoiceLines sets `quantity: e.node.quantityText`, and
	 * `quantityText` is `String` in the schema. It flows on into Util.getInvoiceEntryAmount and
	 * Table.NumberColumn as a string, exactly as the inline mapper this type replaced did. Typed
	 * honestly rather than narrowed, since the point of this type is to make that boundary
	 * checkable.
	 */
	quantity?: number | string;
	unitPrice?: number;
	/** The entry's own price, set once at construction and never rewritten - see applyItemDefaults. */
	originalUnitPrice?: number;
	discount?: number;
	tax?: number;
	accountCode?: string;
	xeroAccount?: string;
	xeroItemId?: string;
	xeroItemCode?: string;
	xeroTaxRate?: number;
	xeroTaxType?: string;
	xeroTracking?: Record<string, string>;
	[key: string]: unknown;
};

/**
 * The legacy modal's invoice lines, mapped onto what the table renders.
 *
 * Extracted from inline JSX because a field dropped here is silently wiped by the merge below on
 * the next interaction, and that defect survived three review rounds precisely because neither
 * half was testable on its own. `toXeroLineRows` cannot be reused: this modal's lines are the
 * untyped shape `getInitialInvoiceLines` builds, not the Relay `InvoiceEntryRow`.
 */
export const toLegacyLineRows = (lines: LegacyInvoiceLine[], projectInDescription: boolean): XeroExportLineRow[] =>
	lines.map(entry => {
		const project = entry.project ?? null;
		return {
			id: entry.id,
			project: project ?? null,
			projectColor: entry.project?.projectColor,
			entryDescription: xeroLineDescription(entry.description, project, projectInDescription),
			quantity: entry.quantity,
			unitPrice: entry.unitPrice,
			originalUnitPrice: entry.originalUnitPrice,
			discountPercentage: (entry.discount || 0) / 100,
			totalPrice: Util.getInvoiceEntryAmount(entry.unitPrice, entry.quantity, entry.discount, entry.tax),
			itemId: entry.xeroItemId,
			itemCode: entry.xeroItemCode,
			accountId: entry.xeroAccount,
			accountCode: entry.accountCode,
			/*
			 * The rate this line is actually taxed at, account or no account. Gating it on an
			 * account showed `-` for a line that Util.getInvoiceTotals was taxing at `entry.tax`,
			 * and - worse - sent `taxRate: undefined` back through applyLegacyLineRows for every
			 * untouched row. An account that carries no rate still renders `-`: the table derives
			 * that from the account list itself, so it does not depend on this value.
			 */
			taxRate: entry.xeroTaxRate ?? entry.tax,
			taxType: entry.xeroTaxType,
			tracking: entry.xeroTracking,
		} as XeroExportLineRow;
	});

/**
 * The table's changes merged back onto the legacy modal's lines.
 *
 * `xeroAccount` and `accountCode` are what the export mutation sends, and `tax` is what
 * `Util.getInvoiceTotals` reduces over, so the totals are written here rather than derived
 * separately.
 */
const taxAfterMerge = (line: LegacyInvoiceLine, updated: XeroExportLineRow, contactTaxRate?: number): number => {
	// An account is selected: its rate is the line's rate. That is the whole point of the column.
	if (updated.taxRate != null) return updated.taxRate;
	/*
	 * An account is selected and carries NO rate. Its answer still governs, and its answer is "no
	 * rate" - which is what the other modal says too: derivedMoneyFields resolves `taxRate ?? 0`.
	 * Falling through to the line's own tax left the Tax cell rendering `-` (rateFor derives it
	 * from the account list) while XeroExportTotals reduced over the contact's rate and showed a
	 * taxed Total. Tax cell untaxed, Total taxed, and the two modals disagreeing about the same
	 * line - the defect the rest of this function exists to prevent.
	 */
	if (updated.accountId) return 0;
	// An account was selected and has just been cleared, so its rate goes with it.
	if (line.xeroAccount) return contactTaxRate ?? 0;
	// No account was ever chosen here, so this row is not about tax - leave the entry's own rate.
	return line.tax ?? contactTaxRate ?? 0;
};

export const applyLegacyLineRows = (
	lines: LegacyInvoiceLine[],
	rows: XeroExportLineRow[],
	contactTaxRate?: number
): LegacyInvoiceLine[] =>
	lines.map(line => {
		const updated = rows.find(candidate => candidate.id === line.id);
		if (!updated) return line;
		return {
			...line,
			xeroItemId: updated.itemId,
			xeroItemCode: updated.itemCode,
			xeroAccount: updated.accountId,
			accountCode: updated.accountCode,
			xeroTaxRate: updated.taxRate,
			xeroTaxType: updated.taxType,
			xeroTracking: updated.tracking,
			/*
			 * Picking an item fills the line's price, so the merge has to carry it back or the
			 * value is discarded and the next `toLegacyLineRows` rebuilds the row from this line's
			 * untouched price - account and tax visibly change, price silently does not.
			 *
			 * `??` for the same reason the tax below is guarded: onRowsChange emits EVERY row, so
			 * this runs over lines the user never touched. Those carry their own price out and
			 * back, making it a no-op; the fallback only matters for a line that never had one.
			 *
			 * `totalPrice` needs no counterpart here - toLegacyLineRows derives it from the line's
			 * own unitPrice on the way back out, so it follows this automatically.
			 */
			unitPrice: updated.unitPrice ?? line.unitPrice,
			/*
			 * NOT `updated.taxRate ?? 0`. The table's onRowsChange emits every row, not the edited
			 * one, so that expression ran over lines the user never touched - and with no account
			 * selected on any of them, a single click on the Item cell of line 1 set `tax` to 0 on
			 * the whole invoice. Util.getInvoiceTotals reduces over `tax`, so the Tax and Total
			 * the user was reading collapsed, and the entry's own rate was gone for good.
			 */
			tax: taxAfterMerge(line, updated, contactTaxRate),
		};
	});

/**
 * The lines whose unit price picking a Product/Service changed, for the export's `accountMap`.
 *
 * The xero service sends this as the line's `UnitAmount` and writes it back to the invoice entry
 * once Xero has accepted the invoice. Nothing on this side writes it, which is why a line the user
 * did not reprice must carry no price key at all.
 *
 * **Compared against the row's own `originalUnitPrice`, never against the Relay entries.** The rows
 * are built once with `useState`, while `ForecastQueryRenderer` defaults to `store-and-network`, so
 * a reopened modal can render cached prices and then receive fresher entries underneath. Comparing
 * against those would report a difference the user never made and export the stale figure over
 * someone else's edit - with no product picked at all. `originalUnitPrice` is whatever the row was
 * built from, so it always answers the only question worth asking: did the user change this here?
 */
export const changedUnitPrices = (
	lines: {id: string; unitPrice?: number | null; originalUnitPrice?: number | null}[]
): {id: string; unitPrice: number}[] =>
	lines.reduce<{id: string; unitPrice: number}[]>((changed, line) => {
		// `typeof`, not a truthiness check: 0 is a price an item can genuinely carry.
		if (typeof line.unitPrice !== 'number') return changed;

		/*
		 * A line whose original price was never recorded is not a repriced line.
		 *
		 * `toXeroLineRows` carries the same fallback, but it only feeds the TABLE - the export calls
		 * this on the modal's own rows, so the guard has to be here too or an absent original makes
		 * every line differ from itself and the whole invoice is sent as repriced.
		 *
		 * Only `undefined` falls back, NOT `null`. They are different claims: undefined is "nobody
		 * recorded an original", while null is "the entry genuinely had no price" - and a line with
		 * no price that a product has now priced IS repriced, and must carry its new price out. `??`
		 * would collapse the two and silently drop that price.
		 */
		const original = line.originalUnitPrice === undefined ? line.unitPrice : line.originalUnitPrice;

		return original === line.unitPrice ? changed : [...changed, {id: line.id, unitPrice: line.unitPrice}];
	}, []);

/**
 * Which organisation to remember after this load - the tenant half of `nextLinesOnTenantLoad`,
 * on its own.
 *
 * Its own export because the caller needs it BEFORE the state updater runs: the ref has to be
 * advanced outside the updater to keep the updater pure under StrictMode's double invoke. It was
 * being obtained by calling `nextLinesOnTenantLoad(previous, next, [])` purely to read `.tenantId`
 * back out of a result whose `lines` were thrown away.
 */
export const nextTenantId = (
	previousTenantId: string | null | undefined,
	incomingTenantId: string | null | undefined
): string | null | undefined => incomingTenantId || previousTenantId;

/**
 * The whole tenant-load step: which lines to keep, and which organisation to remember.
 *
 * A predicate alone was not enough — inverting its call site left every suite green. This returns
 * both halves so the decision is asserted where it is made.
 *
 * An unknown organisation (a Xero contact with no `TenantId`) keeps the PREVIOUS one rather than
 * forgetting it. Storing `undefined` made A → unknown → B stop clearing, because neither hop
 * looked like a change, and A's ids then reached B's export.
 */
export const nextLinesOnTenantLoad = <T extends object>(
	previousTenantId: string | null | undefined,
	incomingTenantId: string | null | undefined,
	lines: T[],
	contactTaxRate?: number
): {lines: T[]; tenantId: string | null | undefined} => {
	if (!incomingTenantId) {
		return {lines, tenantId: previousTenantId};
	}
	return shouldClearOnTenantChange(previousTenantId, incomingTenantId)
		? {lines: clearXeroLineSelections(lines, contactTaxRate), tenantId: incomingTenantId}
		: {lines, tenantId: incomingTenantId};
};

/**
 * The account map as it was before this story, for the flag-off path.
 *
 * Shared because it existed as two separate inline copies, one per modal, which could drift apart
 * with nothing noticing - and AC1 is a statement about exactly this shape, so a drift is an AC
 * failure rather than a tidiness one.
 */
export const toPreFlagAccountMap = (lines: {id: string; xeroAccount?: string; accountCode?: string}[]) =>
	lines.map(line => ({
		entryId: line.id,
		accountId: line.xeroAccount,
		accountCode: line.accountCode,
	}));

/**
 * Whether the Xero export button must stay disabled.
 *
 * The organisation is part of readiness, not an assumption. After moving to a Xero contact with no
 * `TenantId` the lines keep the previous organisation's ids - deliberately, so that switching back
 * restores them - so `!line.xeroAccount` still passes. Without this the button stayed enabled and
 * posted those ids with `tenantId: undefined`, beside visibly empty cells.
 */
export const isXeroExportBlocked = ({
	exporting,
	hasClient,
	tenantId,
	lines,
	alreadyExported = false,
}: {
	exporting: boolean;
	hasClient: boolean;
	tenantId: string | null | undefined;
	lines: {xeroAccount?: string}[];
	/**
	 * Whether Xero already holds this invoice - the invoice's `xeroId`.
	 *
	 * The modal stays open when an export fails, which is right for an ordinary refusal: the user's
	 * picks survive and they can retry. It is wrong for the one failure that matters most - Xero
	 * ACCEPTED, the link was written, and a later step threw - because retrying then asks Xero for a
	 * second invoice for the same work. The flag-on modal gates its own footer on the same field.
	 *
	 * Defaults to false so a caller that cannot answer is not blocked by silence.
	 */
	alreadyExported?: boolean;
}): boolean => exporting || alreadyExported || !hasClient || !tenantId || lines.some(line => !line.xeroAccount);
